"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Shared scroll-triggered reveal wrapper for the landing page (2026
 * refresh). One small primitive used by every section instead of each
 * component hand-rolling its own IntersectionObserver/motion variant,
 * so the "one well-orchestrated" staggered-reveal feel stays consistent.
 *
 * Respects `prefers-reduced-motion` via framer-motion's
 * `useReducedMotion` — reduced-motion users get an instant, non-animated
 * reveal (ui-ux-spec.md accessibility acceptance criteria).
 */

import { motion, useReducedMotion, type Variants } from "framer-motion"
import type { PropsWithChildren } from "react"

// Module-level component map (2026-08-29 owner bug: "the panel comes and
// goes"). `motion.create(as)` used to run INSIDE render, minting a new
// component type every render — React then remounted the subtree, so any
// state change inside a Reveal (e.g. hovering the savings legend) blanked
// the content and replayed the entrance. Component identities must be
// stable across renders.
const MOTION = { div: motion.div, li: motion.li, article: motion.article } as const

interface RevealProps {
  /** Kept to the tag names actually used by call sites (motion.create is typed per-tag, not generically). */
  as?: "div" | "li" | "article"
  /** Stagger index — each step adds a small delay so groups cascade in. */
  index?: number
  y?: number
  className?: string
  once?: boolean
}

export function Reveal({
  as = "div",
  index = 0,
  y = 18,
  className,
  once = true,
  children,
}: PropsWithChildren<RevealProps>) {
  const reduceMotion = useReducedMotion()
  // Fail-safe resting state (craft fix C3, 2026-09-10): the entrance is driven
  // by an IntersectionObserver, so anywhere the observer cannot run the section
  // would stay parked at opacity 0 and the page would render blank. Where it is
  // unavailable — as in some embedded/preview renderers — the content is simply
  // visible from the start. Printing is covered by a `[data-reveal]` rule in
  // globals.css for the same reason. The designed scroll reveal is unchanged.
  const observed = typeof IntersectionObserver !== "undefined"
  const still = !observed || reduceMotion

  const variants: Variants = still
    ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
    : {
        hidden: { opacity: 0, y },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.55, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] },
        },
      }

  const MotionComponent = MOTION[as]

  return (
    <MotionComponent
      data-reveal=""
      className={className}
      initial={still ? "visible" : "hidden"}
      whileInView="visible"
      viewport={{ once, margin: "-80px" }}
      variants={variants}
    >
      {children}
    </MotionComponent>
  )
}
